/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  UseCollectionRendererSelectMultipleParams,
  createKeysFromValueItems,
  isBeforeDataFetch,
  isPhone,
  isSearchTextEmptyOrUndefined,
  useCollectionRenderer,
  useSelectCommon
} from '#PRIVATE_SelectCommon';
import type { DataState } from '#UNSAFE_Collection';
import { useId } from '#hooks/UNSAFE_useId';
import { useUser } from '#hooks/UNSAFE_useUser';
import { KEYS, isControlOrFunctionKey } from '#utils/PRIVATE_keyboardUtils';
import type { Item } from '#utils/UNSAFE_dataProvider';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { merge } from '@oracle/oraclejet-internal-utilities/stringUtils';
import type { Handle } from '@oracle/oraclejet-internal-utilities/typeUtils';
import type { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import type { ComponentProps, RefObject } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { SelectMultiple } from './SelectMultiple';
import { SelectedValuesCount } from './SelectedValuesCount';
import { TextTagList } from './TextTagList';
import { useHiddenTestMethod } from '#hooks/UNSAFE_useHiddenTestMethod';
import { useSelectedOnlyData } from './useSelectedOnlyData';

type TextTagListHandle = Handle<typeof TextTagList, 'ref'>;
type PickedPropsFromSelectMultiple = Pick<
  ComponentProps<typeof SelectMultiple>,
  'addToList' | 'aria-describedby' | 'isDisabled' | 'isReadonly' | 'onAddToListAction' | 'onFilter'
>;
type RenamedPropsFromSelectMultiple = Omit<PickedPropsFromSelectMultiple, 'aria-describedby'> & {
  ariaDescribedBy: PickedPropsFromSelectMultiple['aria-describedby'];
};
export type UseSelectMultipleProps<K, D> = RenamedPropsFromSelectMultiple & {
  data?: DataState<K, D> | null;
  inputRef: RefObject<HTMLElement>;
  isFocused?: boolean;
  onCommit: (detail: ValueUpdateDetail<Set<K>>) => void;
  valueItems?: Item<K, D>[];
};

type SelectedValuesCountToggleType = ComponentProps<typeof SelectedValuesCount>['onToggle'];
type SelectedValuesCountToggleDetailType = Parameters<
  Exclude<SelectedValuesCountToggleType, undefined>
>[0];

/**
 * Hook that manages SelectMultiple state and behavior.  This hook creates state variables and
 * event listeners, returning properties to apply to components internally rendered by
 * SelectMultiple, as well as state information.
 *
 * @param ariaDescribedBy Specifies the ID of elements that describe the input.
 * @param data Specifies data for the dropdown list.
 * @param inputRef Ref to the input element (this can be an input in desktop and a div in mobile).
 * @param isDisabled Specifies whether the component is disabled.
 * @param isFocused Specifies whether the component has focus.
 * @param isReadonly Specifies whether the component is readonly.
 * @param onCommit Callback invoked when the selected values are committed.
 * @param onFilter Callback function to trigger loading data for the dropdown list,
 * which may or may not be filtered by user entered text.
 * @param onLoadRange Callback function to handle when the viewport of the dropdown list has
 * changed, including the case where the user scrolls to the end of the list and there are more
 * items to load.  The function should set a new DataState on the component for the specified
 * range.
 * @param valueItems Specifies the keys, data, and optional metadata for the selected values.
 *
 * @returns Properties to apply to internal components that SelectMultiple renders, and component
 * state.
 */
export function useSelectMultiple<K extends string | number, D extends Record<string, any>>({
  addToList,
  ariaDescribedBy,
  data: propData,
  inputRef,
  isDisabled,
  isFocused: propIsFocused,
  isReadonly,
  onAddToListAction,
  onCommit,
  onFilter,
  valueItems
}: UseSelectMultipleProps<K, D>) {
  const [isDropdownSelectedOnlyView, setDropdownSelectedOnlyView] = useState(false);

  const selectedValuesCount = valueItems?.length;
  const hasSelectedValuesCount =
    (selectedValuesCount !== undefined && selectedValuesCount > 0) || isDropdownSelectedOnlyView;

  // show only the selected values in the dropdown if isDropdownSelectedOnlyView is true,
  // otherwise show the full data set
  // TODO: We may need to revisit this for hierarchical data because the selected valueItems
  // won't have any information about ancestor group nodes, and we should probably show selected
  // items in context within the hierarchy.  Also, the order of the items in the selected-only
  // view right now follows the order in which they were selected, not the order in which they
  // would appear in the full data set.  This may be more of a problem for hierarchical data
  // if items have to appear within the correct group ancestors.  We may need to move
  // construction of the selected-only DataState out of the component to the consuming code,
  // and take a callback that we can call as a notification that the app should provide this
  // special DataState to us (kind of like onLoadRange).  Note that we may also need an
  // enhancement on the DataProvider API to be able to specify a filterCriterion to filter by
  // the selected keys through a fetchFirst call, because a regular fetchByKeys call would
  // still return results in the order in which the keys were specified and would not return
  // data for the ancestor group nodes.
  const selectedOnlyDataProps = useSelectedOnlyData<K, D>({
    isDropdownSelectedOnlyView,
    valueItems
  });
  const data = isDropdownSelectedOnlyView ? selectedOnlyDataProps.data : propData;
  const handleOnFilter = useCallback<NonNullable<typeof onFilter>>(
    (args) => {
      // when the user is filtering, we need to toggle out from selected only view
      // we consider it filtering only if there is a non-empty filter text is present
      if (!isSearchTextEmptyOrUndefined(args.searchText)) {
        setDropdownSelectedOnlyView(false);
      }
      onFilter?.(args);
    },
    [onFilter]
  ); // setDropdownSelectedOnlyView is stable so not needed in dep array

  const {
    currentRowKeyRef,
    currentRowOverride,
    dropdownRef,
    getDropdownTabbableElems,
    handleDropdownArrowClick,
    handleDropdownAutoDismiss: baseHandleDropdownAutoDismiss,
    handleDropdownPosition,
    handleInput,
    handleMainFieldKeyDown: baseHandleMainFieldKeyDown,
    handleMainFieldKeyUp,
    handleUpDownArrowKeys,
    isDropdownAbove,
    isDropdownOpen,
    isFocused,
    isUserFiltering,
    mainFieldRef,
    onCurrentKeyChange,
    onMouseDown,
    searchText,
    setCurrentRowOverride,
    setDropdownOpen,
    setUserInput,
    stopFiltering,
    userInput
  } = useSelectCommon<K, D>({
    data,
    inputRef,
    isFocused: propIsFocused,
    onFilter: handleOnFilter
  });

  const [prevIsDropdownOpen, setPrevIsDropdownOpen] = useState(isDropdownOpen);
  const [prevValueItems, setPrevValueItems] = useState(valueItems);

  const [selectedKeys, setSelectedKeys] = useState<Set<K> | undefined>(
    createKeysFromValueItems<K, D>(valueItems)
  );

  const isDataFetched = data !== undefined && data !== null && !isBeforeDataFetch<K, D>(data);
  const isEmptyResults = isDataFetched && data?.totalSize === 0;
  const isAddToListShown =
    addToList === 'on' && isUserFiltering && searchText != null && searchText.length > 0;

  const handleAddToListAction = useCallback(() => {
    setDropdownOpen(false);
    stopFiltering();
    onAddToListAction?.({ searchText: isUserFiltering ? searchText : undefined });
  }, [isUserFiltering, onAddToListAction, searchText, setDropdownOpen, stopFiltering]);

  const textTagListRef = useRef<TextTagListHandle>(null);
  const { direction } = useUser();
  const selectedValuesDescriptionId = useId();
  const selectedValuesKeyboardNavDescriptionId = useId();
  const combinedAriaDescribedBy = merge([
    // when focused, we will show the text tag list.
    // so, we need the screen readers to read them when focusing on the input.
    isFocused && selectedValuesKeyboardNavDescriptionId,
    isFocused && selectedValuesDescriptionId,
    ariaDescribedBy
  ]);

  const removeDropdown = useCallback(() => {
    dropdownRef.current?.parentElement?.removeChild(dropdownRef.current);
  }, [dropdownRef]);

  const shouldSelectCurrentRow = useCallback(
    (currentRowKey?: K): currentRowKey is K => {
      // current row needs to be selected if all the conditions below are satisfied
      // 1. user is filtering
      // 2. dropdown is open
      // 3. user has entered a search text
      // 4. current data state has data
      // 5. currentRow exists

      return (
        isUserFiltering &&
        isDropdownOpen &&
        !isSearchTextEmptyOrUndefined(userInput) &&
        (data?.data.length ?? 0) > 0 &&
        currentRowKey !== undefined
      );
    },
    [data, isDropdownOpen, isUserFiltering, userInput]
  );

  const selectRow = useCallback(
    (key: K) => {
      // If the key is already selected, do nothing
      if (selectedKeys?.has(key)) return;
      // If it is not already selected, add it to the selection
      const newValue =
        selectedKeys === undefined ? new Set([key]) : new Set([...selectedKeys.values(), key]);
      onCommit({ previousValue: selectedKeys, value: newValue });
      setSelectedKeys(newValue);
    },
    [onCommit, selectedKeys, setSelectedKeys]
  );

  // JET-60789, JET-62113: Create a changeValue method that can be called by a test adapter to
  // simulate a user changing the value. This method calls the onCommit callback, just like what
  // would happen if a user (or test) made a change through the UI.
  const textFieldRef = useRef<HTMLElement>(null);
  const changeValue = useCallback(
    (value?: Set<K>) => {
      // We always want this method available on the DOM element so that a test can call it,
      // but we only want it to change the value when the component is enabled because that's
      // the only time the user could change the value through the UI.
      if (!isReadonly && !isDisabled) {
        onCommit({ value, previousValue: selectedKeys });
        return Promise.resolve();
      }
      return Promise.reject('Component is readonly or disabled');
    },
    [isDisabled, isReadonly, onCommit, selectedKeys]
  );
  // Expose _changeValue as a hidden method on the root DOM.
  useHiddenTestMethod({
    elementRef: isReadonly ? mainFieldRef : textFieldRef,
    method: changeValue,
    name: '_changeValue'
  });

  const myHandleDropdownAutoDismiss = useCallback(
    (event?: Event) => {
      if (isDropdownOpen) {
        if (event?.type === 'keydown') {
          const keyboardEvent = event as KeyboardEvent;
          switch (keyboardEvent.code) {
            case KEYS.TAB: {
              // if there are multiple tabbable elements in the dropdown, like the list and
              // the add to list link, then leave the dropdown open when tabbing among them
              const isShift = keyboardEvent.shiftKey;
              const tabbableElems = getDropdownTabbableElems();
              if (tabbableElems && tabbableElems.length > 1) {
                const firstTabbableElem = tabbableElems[0];
                const lastTabbableElem = tabbableElems[tabbableElems.length - 1];
                // check if tabbing forwards and focus is not on the last tabbable elem or
                // tabbing backwards and focus is not on the first tabbable elem,
                // then keep the dropdown open and simply return
                if (
                  (!isShift && document.activeElement !== lastTabbableElem) ||
                  (isShift && document.activeElement !== firstTabbableElem)
                ) {
                  setDropdownOpen(true);
                  return;
                }
              }

              // Put focus on an element in the main field, but don't kill the event, so that
              // the browser can naturally transfer focus to the next focusable item relative
              // to the element in the main field.
              if (keyboardEvent.shiftKey || !hasSelectedValuesCount) {
                inputRef.current?.focus();
              } else {
                chipRef.current?.focus();
              }

              // Remove the dropdown immediately, without waiting for a rerender based on changing
              // state, so that the open dropdown cannot get focus when the browser handles the
              // Tab key.
              removeDropdown();
              break;
            }
            default:
              break;
          }
        }
      }
    },
    [
      getDropdownTabbableElems,
      hasSelectedValuesCount,
      inputRef,
      isDropdownOpen,
      removeDropdown,
      setDropdownOpen
    ]
  );

  const myHandleMainFieldKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // const target = event.target as HTMLElement;
      // ignore control key and function key
      if (isControlOrFunctionKey(event)) {
        return;
      }

      switch (event.code) {
        case KEYS.TAB:
          if (isDropdownOpen && (event.shiftKey || (!hasSelectedValuesCount && !event.shiftKey))) {
            // Remove the dropdown immediately, without waiting for a rerender based on changing
            // state, so that the open dropdown cannot get focus when the browser handles the
            // Tab key.
            removeDropdown();

            setDropdownOpen(false);
          }

          // JET-53183 - core pack compat: stop propagation of handled events
          // we don't want a parent collection component to jump focus to the next cell and skip
          // over the selected values count chip
          if (hasSelectedValuesCount && !event.shiftKey) {
            event.stopPropagation();
          }
          break;

        case KEYS.LEFT:
          // Move focus to the text tag if we are in LTR and the cursor is at the
          // beginning of the text (and there is no selection)
          if (
            direction === 'ltr' &&
            inputRef.current instanceof HTMLInputElement &&
            inputRef.current?.selectionStart === 0 &&
            inputRef.current?.selectionEnd === 0
          ) {
            textTagListRef.current?.focus();
          }
          break;

        case KEYS.RIGHT:
          // Move focus to the text tag if we are in RTL and the cursor is at the
          // beginning of the text (and there is no selection)
          if (
            direction === 'rtl' &&
            inputRef.current instanceof HTMLInputElement &&
            inputRef.current?.selectionStart === 0 &&
            inputRef.current?.selectionEnd === 0
          ) {
            textTagListRef.current?.focus();
          }
          break;

        case KEYS.ENTER:
        case KEYS.NUMPAD_ENTER:
          // if this is on a mobile device (only phone and not a tablet device), do not do anything
          if (isPhone()) {
            return;
          }

          // if add to list is shown and there are no results, trigger the add to list link
          if (isAddToListShown && isEmptyResults) {
            handleAddToListAction();
            return;
          }

          // if there is a currentRow in the dropdown (meaning a highlighted item that would
          // have keyboard focus if the dropdown list had physical focus, which may be different
          // from the currently selected item), select it and commit it;
          if (shouldSelectCurrentRow(currentRowKeyRef.current)) {
            setDropdownOpen(false);
            // after selecting a value, the user is no longer actively filtering, so update related
            // state accordingly so that the newly selected value appears in the field
            stopFiltering();
            selectRow(currentRowKeyRef.current);
          }
          break;

        default:
          break;
      }
    },
    [
      currentRowKeyRef,
      direction,
      handleAddToListAction,
      hasSelectedValuesCount,
      inputRef,
      isAddToListShown,
      isDropdownOpen,
      isEmptyResults,
      removeDropdown,
      selectRow,
      setDropdownOpen,
      shouldSelectCurrentRow,
      stopFiltering
    ]
  );

  // when the array of valueItems changes, pull out the new set of selectedKeys
  // (don't need to set selectedKeys here on first render because the state was initialized with
  // the correct value)
  if (prevValueItems !== valueItems) {
    const keys = createKeysFromValueItems<K, D>(valueItems);
    setSelectedKeys(keys);
  }

  const chipRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    chipRef.current =
      hasSelectedValuesCount && mainFieldRef.current?.querySelector
        ? mainFieldRef.current.querySelector('[role="switch"]')
        : null;
  }, [hasSelectedValuesCount, isDisabled, isReadonly, mainFieldRef]);

  const handleSelectedValuesCountKeyDown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.code) {
        case KEYS.TAB:
          if (isDropdownOpen && !event.shiftKey) {
            // Remove the dropdown immediately, without waiting for a rerender based on changing
            // state, so that the open dropdown cannot get focus when the browser handles the
            // Tab key.
            removeDropdown();

            setDropdownOpen(false);
          }

          // JET-53183 - core pack compat: stop propagation of handled events
          // we don't want a parent collection component to jump focus to the next cell and skip
          // over the input
          if (event.shiftKey) {
            event.stopPropagation();
          }
          break;

        case KEYS.UP:
        case KEYS.DOWN:
          // prevent the page from scrolling
          event.preventDefault();
          break;

        case KEYS.ESC:
          if (isDropdownOpen) {
            setDropdownOpen(false);

            event.preventDefault();

            // JET-53183 - core pack compat: stop propagation of handled events
            // we don't want a parent collection component to stop editing the row when we close
            // the dropdown
            event.stopPropagation();
          }
          break;

        case KEYS.ENTER:
          // JET-53183 - core pack compat: stop propagation of handled events
          // we don't want a parent collection component to move focus to the next row when
          // we toggle between the full and selected only list in the dropdown
          event.stopPropagation();
          break;

        default:
          break;
      }
    },
    [isDropdownOpen, removeDropdown, setDropdownOpen]
  );

  const handleSelectedValuesCountKeyUp = useCallback(
    (event: KeyboardEvent) => {
      switch (event.code) {
        case KEYS.UP:
        case KEYS.DOWN:
          handleUpDownArrowKeys(event);
          break;

        default:
          break;
      }
    },
    [handleUpDownArrowKeys]
  );

  const handleSelectedValuesCountMouseDown = useCallback(
    (event: MouseEvent) => {
      // call preventDefault so that the dropdown doesn't open showing the full list on mousedown
      // on the chip before switching to show only the selected values on mouseup when the click
      // is handled
      event.preventDefault();
      // explicitly focus the chip because it won't happen naturally due to preventDefault()
      chipRef.current?.focus();
    },
    [chipRef]
  );

  const handleSelectedValuesCountToggle = useCallback(
    (detail: SelectedValuesCountToggleDetailType) => {
      if (!isDropdownOpen) {
        setDropdownOpen(true);
      }

      const value = detail.value ?? false;
      if (value !== isDropdownSelectedOnlyView) {
        setDropdownSelectedOnlyView(value);

        // if the selected only toggle is enabled, we need to stop user filtering
        if (value) {
          stopFiltering();
        }
      }
    },
    [isDropdownOpen, isDropdownSelectedOnlyView, setDropdownOpen, stopFiltering]
  );

  // when the dropdown closes, automatically turn off isDropdownSelectedOnlyView
  if (!isDropdownOpen && isDropdownSelectedOnlyView) {
    setDropdownSelectedOnlyView(false);
  }

  const onSelectedKeysChange = useCallback<
    UseCollectionRendererSelectMultipleParams<K, D>['onSelectedKeysChange']
  >(
    (detail) => {
      // update the selectedKeys so that the selection visual state of the collection
      // updates immediately while waiting for new valueItems to be set
      setSelectedKeys(detail.value);

      // clear the filter text typed by the user, but do NOT clear the actual filter criterion
      // or highlighting in the list
      setUserInput('');

      // call the onCommit callback so that the consuming code can update the valueItems
      onCommit?.({ value: detail.value, previousValue: selectedKeys });
    },
    [onCommit, selectedKeys, setSelectedKeys, setUserInput]
  );

  const onTextTagListExitNavigation = useCallback(() => {
    // Focus the input on the next tick. Doing it in the same event cycle will
    // trigger the key press on the input element which makes the caret move by
    // one character.
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [inputRef]);

  const onTextTagListRemove = useCallback(
    (keys: K[]) => {
      const previousValue = selectedKeys;
      const value = new Set(selectedKeys);
      keys.forEach((key) => value.delete(key));
      onCommit?.({ previousValue, value });
    },
    [onCommit, selectedKeys]
  );

  const dropdownEventHandlers = useMemo(() => {
    return mergeProps(
      {
        onAutoDismiss: baseHandleDropdownAutoDismiss,
        onPosition: handleDropdownPosition
      },
      { onAutoDismiss: myHandleDropdownAutoDismiss }
    );
  }, [baseHandleDropdownAutoDismiss, handleDropdownPosition, myHandleDropdownAutoDismiss]);

  const inputEventHandlers = useMemo(() => {
    return mergeProps(
      {
        onInput: handleInput,
        onKeyDown: baseHandleMainFieldKeyDown,
        onKeyUp: handleMainFieldKeyUp
      },
      { onKeyDown: myHandleMainFieldKeyDown }
    );
  }, [baseHandleMainFieldKeyDown, handleInput, handleMainFieldKeyUp, myHandleMainFieldKeyDown]);

  const textTagListProps = useMemo(
    () => ({
      textTagListRef,
      onExitNavigation: onTextTagListExitNavigation,
      onRemove: onTextTagListRemove
    }),
    [onTextTagListExitNavigation, onTextTagListRemove]
  );

  const collectionRendererContext = useCollectionRenderer<K, D>({
    currentKey: currentRowOverride.rowKey,
    onCurrentKeyChange,
    onSelectedKeysChange,
    searchText,
    selectedKeys,
    selectedOnlyData: selectedOnlyDataProps.data
  });

  // clear the current item when the dropdown opens
  if (isDropdownOpen && !prevIsDropdownOpen) {
    setCurrentRowOverride({ rowKey: undefined });
  }

  // when the dropdown closes, if the user input is empty, reset filtering
  const isDropdownClosing = prevIsDropdownOpen && !isDropdownOpen;
  if (isDropdownClosing && !userInput) {
    stopFiltering();
  }

  // We need to highlight the first result whenever the data changes
  // We cannot use state for the prevData and update it during the render as it is
  // returned by a useMemo hook.
  // There is a bug in preact where if a return value of a useMemo is used for
  // updating a state during the render, it creates an infinite render cycle.
  // See https://github.com/preactjs/preact/issues/4422.
  // Till this issue is fixed, we need to use a ref for this use case.
  const prevData = useRef(data);
  if (prevData.current !== data) {
    // when filtering, set the first result of the filtered data set to be the current item
    // (check the data offset so we don't try to highlight again if the user scrolls through
    // the list and more data is fetched)
    const firstResultKey = data?.data[0]?.metadata.key;
    if (isDropdownOpen && isUserFiltering && firstResultKey != null && data?.offset === 0) {
      // if there are results and search text, highlight the first row, otherwise clear
      // an existing highlight
      setCurrentRowOverride({
        rowKey: isSearchTextEmptyOrUndefined(searchText) ? undefined : firstResultKey
      });
    }

    prevData.current = data;
  }

  if (prevIsDropdownOpen !== isDropdownOpen) {
    setPrevIsDropdownOpen(isDropdownOpen);
  }
  if (prevValueItems !== valueItems) {
    setPrevValueItems(valueItems);
  }

  return {
    addToListEventHandlers: {
      onAction: handleAddToListAction
    },
    ariaProps: {
      ariaDescribedBy: combinedAriaDescribedBy
    },
    collectionRendererContext,
    data,
    dropdownArrowEventHandlers: {
      onClick: handleDropdownArrowClick
    },
    dropdownEventHandlers,
    dropdownRef,
    hasSelectedValuesCount,
    inputEventHandlers,
    inputRef,
    isAddToListShown,
    isDataFetched,
    isDropdownAbove,
    isDropdownOpen,
    isDropdownSelectedOnlyView,
    isEmptyResults,
    isFocused,
    isUserFiltering,
    mainFieldRef,
    mouseProps: isDisabled || isReadonly ? {} : { onMouseDown },
    searchText,
    selectedValuesCountProps: {
      onKeyDown: handleSelectedValuesCountKeyDown,
      onKeyUp: handleSelectedValuesCountKeyUp,
      onMouseDown: handleSelectedValuesCountMouseDown,
      onToggle: handleSelectedValuesCountToggle
    },
    selectedValuesDescriptionId,
    selectedValuesKeyboardNavDescriptionId,
    setDropdownOpen,
    setUserInput,
    stopFiltering,
    textFieldRef,
    textTagListProps,
    userInput
  };
}
